import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MdEdit } from 'react-icons/md';
import { FaRegTrashCan } from 'react-icons/fa6';
import {
    UPDATE_HOSPITAL_USER_PATH,
    UPDATE_NURSE_PATH,
    UPDATE_USER_PATH,
} from '@constants/routes';
import {
    adminDeleteUser,
    fetchCountries,
    getUserInfo,
    getUserName,
    handleFetchLogo,
} from '@utils/utils';
import {
    APPROVED,
    BLOCKED,
    HOSPITAL,
    INDIVIDUAL,
    NURSE,
    REJECTED,
    WAITING,
    WAITING_FOR_APPROVAL,
} from '@constants/constants';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
    setHospitalDetail,
    setNurseDetail,
    setUserDetail,
    setUserDeviceList,
    setUserEmail,
    setUserMobileNumber,
} from '@store/reducers/userSlice';
import {
    setAddressOne,
    setAddressTwo,
    setAdminCreateHospital,
    setAdminCreateIndividual,
    setAdminCreateNurse,
    setCity,
    setCountry,
    setDeviceUniqueID,
    setEmail,
    setHospitalBranch,
    setHospitalContact,
    setHospitalEmail,
    setHospitalLogoName,
    setHospitalName,
    setHospitalUserTableID,
    setHospitalWebsite,
    setMobileNumber,
    setName,
    setOldLogoName,
    setPincode,
    setUserRole,
    setUserState,
    setUserTableID,
    setHospitalID,
    setCountryList,
    setHospitalAddNurse,
    setOldEmail,
    setShowUpdatePassword,
} from '@store/reducers/auth/signupSlice';
import {
    addDeletionReason,
    getUserAttributes,
    handleDeleteUser,
} from '@utils/authUtils';
import ModalPopUp from '@components/ModalPopUpComponent';
import TextAreaPopUp from '@components/TextAreaPopUpComponent';
import { TENANT_ID } from '@constants/cognitoAttributes';
import {
    capitalizeFirstLetter,
    getDataUsingTenantId,
    getDataUsingUserId,
} from '@utils/sharedUtils';
import DataLoader from '@components/DataLoader';
import Swal from 'sweetalert2';
import { useAuditLog } from '../../contexts/AuditLogContext';
const MySwal = Swal;

// interface UserData {
//     id: number;
//     name: string;
//     email: string;
// }

// interface UserTableProps {
//     data: UserData[];
// }

interface UserDeviceList {
    id: number;
    name: string;
    date: string;
}
interface UserTableProps {
    data: any[];
    header: any[];
    row: number;
    totalData: any[];
}

interface UserData {
    id: string;
    name: string;
    email: string;
    createdAt: string;
    deviceId: string;
    mobileNo: string;
    role: string;
    slug: string;
    tenantId: string;
}

interface UserInfo {
    hospital_logo?: { S: string };
    hospital_name?: { S: string };
    hospital_branch?: { S: string };
    hospital_website?: { S: string };
    gst_no?: { S: string };
    hospital_contact?: { S: string };
    hospital_email?: { S: string };
    address1?: { S: string };
    address2?: { S: string };
    city?: { S: string };
    state?: { S: string };
    country?: { S: string };
    pincode?: { S: string };
    status?: { S: string };
}

type UserInfoResponse = UserInfo[];

const NurseManagementTable = ({
    data,
    header,
    row,
    totalData,
}: UserTableProps) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showTextModal, setShowTextModal] = useState(false);
    const [allData, setAllData] = useState(totalData);
    const [rowPerPage, setRowPerPage] = useState(row);
    const [updatedData, setData] = useState(data);
    const [userData, setUserData] = useState<UserData | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const { addLog } = useAuditLog();

    useEffect(() => {
        if (data) setData(data); // Ensure this reflects the updated state correctly
        if (totalData) setAllData(totalData); // Ensure this reflects the updated state correctly
        if (row) setRowPerPage(row);
    }, [data, row, totalData]);

    if (updatedData.length == 0) {
        return (
            <div className="card rounded-2xl w-full mx-auto p-0 !mt-5">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="border-top m-3">
                            <tr>
                                {header.map((user) => (
                                    <th
                                        className="text-black text-base font-semibold py-4 !normal-case"
                                        key={user.id}
                                    >
                                        {user.title}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="text-center">
                                <td colSpan={header.length + 1}>
                                    {t('user_management.user_table.no_data')}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Get the keys from the first object in the data array
    let keys = Object.keys(updatedData[0]).filter(
        (key) =>
            key !== 'id' &&
            key !== 'slug' &&
            key !== 'address' &&
            key !== 'createdAt_time' &&
            key !== 'role' &&
            key !== 'userEmailStatus' &&
            key !== 'hospitalName' &&
            key !== 'tenantId'
    );

    const hasHospital = updatedData.some(
        (user) => user.role.toLowerCase() === HOSPITAL.toLowerCase()
    );
    const hasNurse = updatedData.some(
        (user) => user.role.toLowerCase() === NURSE.toLowerCase()
    );

    // Conditionally filter out 'device_id' if 'hospital_admin_status' is present
    if (hasHospital) {
        keys = keys.filter(
            (key) =>
                key !== 'deviceId' &&
                key !== 'mobileNo' &&
                key !== 'createdAt' &&
                key !== 'hospital_branch' &&
                key !== 'hospital_website' &&
                key !== 'gst_no' &&
                key !== 'name' &&
                key !== 'approved'
        );
    } else if (hasNurse) {
        keys = keys.filter(
            (key) =>
                key !== 'role' &&
                key !== 'tenantId' &&
                key !== 'deviceId' &&
                key !== 'mobileNo' &&
                key !== 'createdAt' &&
                key !== 'userEmailStatus' &&
                key !== 'createdBy' &&
                key !== 'email'
        );
    }

    const getStatusClass = (status: string) => {
        // console.log('Status:', status);
        switch (status.toLowerCase().trim()) {
            case APPROVED.toLowerCase():
                return 'text-white bg-success inline-block rounded-2xl px-3 py-1';
            case WAITING.toLowerCase():
                return 'text-white bg-warning inline-block rounded-2xl px-3 py-1';
            case WAITING_FOR_APPROVAL.toLowerCase():
                return 'text-white bg-warning inline-block rounded-2xl px-3 py-1';
            case BLOCKED.toLowerCase():
                return 'text-white bg-secondary inline-block rounded-2xl px-3 py-1';
            case REJECTED.toLowerCase():
                return 'text-white bg-danger inline-block rounded-2xl px-3 py-1';
            default:
                return '';
        }
    };

    const handleRowClick = async (user: any) => {
        if (user.role.toLowerCase() == NURSE.toLowerCase()) {
            const res = await getDataUsingUserId('user_info', user.id);

            if (res && res.Items && res.Items.length > 0) {
                const additionalDetails = res.Items[0];

                let approved_By = '--';
                if (additionalDetails.approved_by?.S) {
                    const assigned = await getDataUsingUserId(
                        'user_info',
                        additionalDetails.approved_by?.S
                            ? additionalDetails.approved_by?.S
                            : ''
                    );

                    if (
                        assigned &&
                        assigned.Items &&
                        assigned.Items.length > 0
                    ) {
                        const data = assigned.Items[0];
                        approved_By = data.name?.S ? data.name?.S : '';
                    }
                }

                const created = await getDataUsingUserId(
                    'user_info',
                    additionalDetails.created_by?.S
                        ? additionalDetails.created_by?.S
                        : ''
                );

                let created_By = '--';
                if (created && created.Items && created.Items.length > 0) {
                    const userdata = created.Items[0];
                    created_By = userdata.name?.S ? userdata.name?.S : '';
                }

                const mergedUserDetails = {
                    id: user.id,
                    tenantUUid: additionalDetails.tenant_id?.S
                        ? additionalDetails.tenant_id?.S
                        : '',
                    name: user.name,
                    status: additionalDetails?.status?.S
                        ? additionalDetails?.status?.S
                        : '--',
                    deviceId: user.deviceId,
                    hospitalId: additionalDetails.hospital_id?.S
                        ? additionalDetails.hospital_id?.S
                        : '--',
                    hospitalName: additionalDetails.hospital_name?.S
                        ? additionalDetails.hospital_name?.S
                        : '--',
                    email_id: user.email,
                    contact_id: user.mobileNumber,
                    approvedBy: approved_By,
                    createdBy: created_By,
                    createdAt: user.createdAt,
                    nurseId: additionalDetails.nurse_id?.S
                        ? additionalDetails.nurse_id?.S
                        : '',
                    address:
                        additionalDetails && additionalDetails.address1
                            ? `${additionalDetails.address1?.S || ''}, ${additionalDetails.address2?.S || ''}, ${additionalDetails.city?.S || ''}, ${additionalDetails.state?.S || ''}, ${additionalDetails.country?.S || ''} - ${additionalDetails.pincode?.S || ''}`.trim()
                            : '--',
                };
                dispatch(setNurseDetail(mergedUserDetails));
                navigate(`/management/nurse/${user.slug}`);
            }
        }
    };

    const handleUpdateUser = async (user: any) => {
        dispatch(setUserRole(NURSE));
        // const res = await getUserInfo('user_info', user.tenantId);
        const res = await getDataUsingUserId('user_info', user.id);
        // console.log('user', user);
        // console.log('res', res);
        if (res && res.Items && res.Items.length > 0) {
            const additionalDetails = res.Items[0];
            dispatch(setUserTableID(additionalDetails.uuid?.S || ''));
            dispatch(setAdminCreateNurse(true));
            dispatch(setHospitalAddNurse(true));
            dispatch(setShowUpdatePassword(false));
            dispatch(setDeviceUniqueID(user.deviceId));
            dispatch(setEmail(user.email));
            dispatch(setOldEmail(user.email));
            dispatch(setMobileNumber(user.mobileNo));
            dispatch(setName(additionalDetails.name?.S || ''));
            dispatch(setAddressOne(additionalDetails.address1?.S || ''));
            dispatch(setHospitalName(additionalDetails.hospital_name?.S || ''));
            dispatch(setHospitalID(additionalDetails.hospital_id?.S || ''));
            dispatch(setAddressTwo(additionalDetails.address2?.S || ''));
            dispatch(setCity(additionalDetails.city?.S || ''));
            dispatch(setUserState(additionalDetails.state?.S || ''));
            dispatch(
                setCountry(
                    additionalDetails.country?.S
                        ? additionalDetails.country?.S
                        : ''
                )
            );
            dispatch(setPincode(additionalDetails.pincode?.S || ''));
            navigate(UPDATE_NURSE_PATH);
        }
    };

    const handleDelete = async (reason: string) => {
        // adminDeleteUser(user.email);
        setShowDeleteModal(false);
        setShowTextModal(false);
        setLoading(true);
        try {
            const attributes = await getUserAttributes();

            if (typeof attributes !== 'string' && attributes !== null) {
                const updatedBy = attributes['custom:tenant_id'] ?? '';

                if (userData) {
                    // console.log('data', userData);
                    await addDeletionReason(updatedBy, reason, userData.id);
                    await handleDeleteUser(
                        userData.tenantId,
                        userData.id,
                        userData.role
                    ).then(async () => {
                        const updatedUserData = updatedData.filter(
                            (item) => item.id !== userData.id
                        );

                        await adminDeleteUser(userData.email);

                        await addLog(
                            'User Deleted',
                            userData.id,
                            'User',
                            loggedInUserID,
                            { 'User': userData.email, 'Reason': reason },
                            window.location.hostname
                        );

                        setData(updatedUserData);
                    });

                    MySwal.fire({
                        position: 'top-end', // Position the toast in the top-right corner
                        icon: 'success',
                        title: 'Nurse deleted successfully',
                        showConfirmButton: false,
                        timer: 1500,
                        toast: true,
                        customClass: {
                            popup: 'text-green bg-light_green', // Add custom class to the popup
                        },
                    });
                }
            }
        } catch (error: any) {
            console.log('error', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFetchCountries = async () => {
        const list = await fetchCountries();
        if (list.countries) {
            dispatch(setCountryList(list.countries));
        } else if (list.error) {
            // Handle error message
            console.error('Error fetching countries:', list.error);
        }
    };

    const deleteUser = (user: any) => {
        setUserData(user);
        setShowDeleteModal(true);
    };
    const tableHeight =
        allData.length >= 10 && rowPerPage === 10
            ? '36rem'
            : allData.length >= 5 && rowPerPage === 5
              ? '20rem'
              : 'auto';

    return (
        <>
            {/* <div className="card rounded-2xl w-full mx-auto p-4 !mt-4"> */}
            <div
                className="card rounded-2xl w-full mx-auto p-0 !mt-4"
                style={{ height: tableHeight }}
            >
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="m-3">
                            <tr>
                                {/* <th></th> */}
                                {header.map((user) => (
                                    <th
                                        className="text-black text-base font-semibold py-4 !normal-case"
                                        key={user.id}
                                    >
                                        {user.title}
                                    </th>
                                ))}
                                {/* <th></th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {updatedData.map((user) => (
                                <tr key={user.id}>
                                    {/* <td></td> */}
                                    {keys.map((key) => {
                                        const value = user[key];
                                        const isStatusKey =
                                            key.toLowerCase().trim() ===
                                            'status';
                                        return (
                                            <td
                                                key={key}
                                                onClick={() =>
                                                    handleRowClick(user)
                                                }
                                                className="cursor-pointer"
                                            >
                                                <div
                                                    className={`text-base font-normal text-table_header 
                                                    ${
                                                        isStatusKey
                                                            ? getStatusClass(
                                                                  value
                                                              )
                                                            : ''
                                                    }
                                                    `}
                                                >
                                                    {value ?? 'N/A'}
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td>
                                        <div className="flex space-x-5 items-center">
                                            <div
                                                className="cursor-pointer"
                                                onClick={() =>
                                                    handleUpdateUser(user)
                                                }
                                            >
                                                <MdEdit className="text-black" />
                                            </div>

                                            <div
                                                className="cursor-pointer"
                                                onClick={() => deleteUser(user)}
                                            >
                                                <FaRegTrashCan className="text-danger" />
                                            </div>
                                        </div>
                                    </td>
                                    {/* <td></td> */}
                                </tr>
                            ))}
                            <tr></tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <ModalPopUp
                show={showDeleteModal}
                message={t('pop_up.user_delete_msg')}
                onConfirm={() => {
                    setShowTextModal(true);
                    setShowDeleteModal(false);
                }}
                onCancel={() => setShowDeleteModal(false)}
            />
            <TextAreaPopUp
                show={showTextModal}
                message={t('pop_up.delete_reason_msg')}
                onConfirm={handleDelete}
                onCancel={() => {
                    setShowTextModal(false);
                    setShowDeleteModal(false);
                }}
            />

            <div />
            {loading && <DataLoader />}
        </>
    );
};

export default NurseManagementTable;
